package co.maytes.standupbot.dashboard.features.home

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.motion.Staggered
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideBarChart
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCalendar
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideFileText
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlug
import co.maytes.standupbot.dashboard.components.widgets.icons.LucidePlay
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTag
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.calendar.TodaysCalendarCard
import co.maytes.standupbot.dashboard.features.standupruns.ProviderCauseChip
import co.maytes.standupbot.dashboard.features.standupruns.StandupStatusChip
import co.maytes.standupbot.dashboard.styles.BrandHue
import co.maytes.standupbot.dashboard.styles.HomeArt
import co.maytes.standupbot.dashboard.styles.HomeClasses
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UnlinkedPeopleDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import co.maytes.standupbot.shared.dto.workspace.MemberStatusDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import co.maytes.standupbot.shared.util.RelativeTime
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

private const val SLACK_LOGO = "/brand/slack.svg"
private const val OVERFLOW_AVATAR_LIMIT = HomeArt.AVATAR_STACK_MAX

data class HomeLiveData(
    val now: Instant,
    val tz: TimeZone,
    val workspaceId: String?,
    val workspaceTimezone: String,
    val runs: List<StandupRunSummaryDto>,
    val standups: List<StandupDto>,
    val members: List<WorkspaceMemberDto>?,
    val canManage: Boolean,
    val unlinked: UnlinkedPeopleDto? = null,
)

@Composable
fun HomeLiveView(data: HomeLiveData) {
    val nav = rememberNavigator()
    val upcoming = HomeModel.upcoming(data.standups, data.now)
    HmDiv(HomeClasses.LIVE) {
        NextStandupCard(data, upcoming, nav, index = 0)
        LatestRunsCard(data, nav, index = 1)
        ActivityCard(data, index = 2)
        data.members?.let { members -> TeamCard(members, data.canManage, nav, index = 3) }
        QuickActionsCard(data.canManage, index = 4)
        data.unlinked?.takeIf { UnlinkedPeopleModel.visible(it) }?.let { UnlinkedPeopleCard(it, index = 5) }
        HmDiv(HomeClasses.SPAN_12, modifier = Modifier.staggerIn(6)) {
            TodaysCalendarCard(workspaceId = data.workspaceId, timezoneId = data.workspaceTimezone)
        }
    }
}

@Composable
private fun NextStandupCard(data: HomeLiveData, upcoming: List<StandupDto>, nav: (String) -> Unit, index: Int) {
    HmDiv(
        HomeClasses.PANEL, HomeClasses.LIFT, HomeClasses.PAD, HomeClasses.SPAN_7,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead("Next standup", BrandHue.LINEAR, { LucideCalendar(size = HomeArt.ICON_PX) })
        StateSwap(state = upcoming.isEmpty()) {
            val first = upcoming.firstOrNull()
            if (first == null) {
                NextStandupEmpty(data, nav)
            } else {
                NextStandupBody(data, first, upcoming.size - 1, nav)
            }
        }
    }
}

@Composable
private fun NextStandupBody(data: HomeLiveData, standup: StandupDto, more: Int, nav: (String) -> Unit) {
    val standupTz = runCatching { TimeZone.of(standup.timezone) }.getOrDefault(data.tz)
    val label = HomeModel.nextRunLabel(standup.nextRunAt!!, data.now, standupTz)
    val active = HomeModel.activeDays(standup.scheduleCron)
    val today = HomeModel.todayRun(data.runs, data.now, data.tz)
    HmDiv(modifier = Modifier.stack()) {
        HmDiv(HomeClasses.BIG_TIME) { Text("${label.whenLabel} · ${label.timeLabel}") }
        HmMuted("${label.relative} · ${shortTimezone(standup.timezone)}")
        HmDiv(modifier = Modifier.rowWrap()) {
            HmStrong(standup.name)
            ChannelChip(standup.slackChannelName ?: standup.slackChannelId)
        }
        HmDiv(HomeClasses.DAYS) {
            DAY_LETTERS.forEachIndexed { day, letter ->
                HmDiv(
                    *classesOf(HomeClasses.DAY, if (day in active) HomeClasses.DAY_ON else null),
                    modifier = Modifier.ariaLabel(DAY_NAMES[day]),
                ) { Text(letter) }
            }
        }
        HmDiv(modifier = Modifier.rowWrap()) {
            today?.let {
                HmMuted("Today")
                StandupStatusChip(status = it.status)
            }
            if (more > 0) HmMuted("+$more more scheduled")
        }
        HmDiv(modifier = Modifier.rowWrap()) {
            Button(
                label = "View standups",
                variant = ButtonVariant.Secondary,
                size = ButtonSize.Sm,
                onClick = { nav("/app/standups") },
            )
        }
    }
}

@Composable
private fun NextStandupEmpty(data: HomeLiveData, nav: (String) -> Unit) {
    val noStandups = data.standups.isEmpty()
    HmEmpty(
        title = if (noStandups) "No standups yet" else "Nothing scheduled",
        subtitle = if (noStandups) "Create one and its schedule shows here." else "Enable a schedule to see it here.",
        actionLabel = when {
            noStandups && data.canManage -> "New standup"
            noStandups -> null
            else -> "View standups"
        },
        onAction = { nav(if (noStandups) "/app/standups/new" else "/app/standups") },
    ) {
        HmDiv(HomeClasses.DAYS) { repeat(HomeArt.DAY_COUNT) { HmDiv(HomeClasses.DAY) {} } }
        HmGhost(widthPercent = 58, height = "1.5rem", accent = true)
        HmGhost(widthPercent = 36, height = "0.75rem")
    }
}

@Composable
private fun LatestRunsCard(data: HomeLiveData, nav: (String) -> Unit, index: Int) {
    val runs = data.runs.take(HomeArt.RUNS_SHOWN)
    HmDiv(
        HomeClasses.PANEL, HomeClasses.LIFT, HomeClasses.PAD, HomeClasses.SPAN_5,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead(
            title = "Latest runs",
            tint = BrandHue.SLACK,
            icon = { LucideMessagesSquare(size = HomeArt.ICON_PX) },
            trailing = if (runs.isEmpty()) null else {
                {
                    Button(
                        label = "All runs",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        onClick = { nav("/app/runs") },
                    )
                }
            },
        )
        StateSwap(state = runs.isEmpty()) {
            if (runs.isEmpty()) {
                LatestRunsEmpty(data, nav)
            } else {
                RunRows(runs, data.standups.associateBy { it.id }, data)
            }
        }
    }
}

@Composable
private fun RunRows(runs: List<StandupRunSummaryDto>, standups: Map<String, StandupDto>, data: HomeLiveData) {
    HmDiv(modifier = Modifier.stack(Spacing.xxs)) {
        Staggered(items = runs, key = { it.id }) { run ->
            val standupTz = run.standupId?.let { standups[it] }?.let { runCatching { TimeZone.of(it.timezone) }.getOrNull() }
            val whenTime = run.postedAt ?: run.scheduledFor
            HmLink("/app/runs/${run.id}", HomeClasses.RUN) {
                StandupStatusChip(status = run.status)
                ProviderCauseChip(run.cause)
                HmDiv(HomeClasses.ROW_TEXT) {
                    HmStrong(run.standupName)
                    HmMuted(RelativeTime.format(whenTime, data.now, standupTz ?: data.tz))
                }
                HmMuted(sectionsLabel(run.sectionsCount))
            }
        }
    }
}

@Composable
private fun LatestRunsEmpty(data: HomeLiveData, nav: (String) -> Unit) {
    HmEmpty(
        title = "No runs yet",
        subtitle = "Posted standups appear here.",
        actionLabel = if (data.standups.isEmpty() && !data.canManage) null else "View standups",
        onAction = { nav("/app/standups") },
    ) {
        HmDiv(modifier = Modifier.rowWrap()) {
            HmDiv(HomeClasses.GHOST_AVATAR) {}
            HmDiv(modifier = Modifier.stack(Spacing.xxs).fill()) {
                HmGhost(widthPercent = 44, height = "0.75rem", accent = true)
                HmGhost(widthPercent = 80, height = "0.625rem")
            }
        }
        HmGhost(widthPercent = 92, height = "0.625rem")
        HmGhost(widthPercent = 64, height = "0.625rem")
    }
}

@Composable
private fun ActivityCard(data: HomeLiveData, index: Int) {
    val activity = ActivityBars.from(data.runs, data.now, data.tz)
    HmDiv(
        HomeClasses.PANEL, HomeClasses.LIFT, HomeClasses.PAD, HomeClasses.SPAN_4,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead(
            title = "Activity",
            tint = Palette.accent.cssRgb(),
            icon = { LucideBarChart(size = HomeArt.ICON_PX) },
            trailing = { HmMuted(postedLabel(activity.total)) },
        )
        StateSwap(state = activity.isEmpty) {
            HmDiv(modifier = Modifier.stack(Spacing.xs)) {
                HmDiv(HomeClasses.BARS) {
                    activity.bars.forEachIndexed { i, bar ->
                        val zero = bar.count == 0
                        HmDiv(
                            HomeClasses.BAR,
                            modifier = Modifier.styleModifier {
                                property(HomeArt.PROGRESS_VAR, "${activity.heightPercent(bar)}%")
                                property(HomeArt.INDEX_VAR, "$i")
                            },
                        ) {
                            HmDiv(
                                *classesOf(
                                    HomeClasses.BAR_FILL,
                                    if (zero) HomeClasses.BAR_ZERO else null,
                                    if (bar.isToday && !zero) HomeClasses.BAR_TODAY else null,
                                ),
                            ) {}
                        }
                    }
                }
                HmDiv(modifier = Modifier.rowBetween()) {
                    HmMuted("${HomeArt.BAR_DAYS} days ago")
                    HmMuted(if (activity.isEmpty) "No posts yet" else "Today")
                }
            }
        }
    }
}

@Composable
private fun TeamCard(members: List<WorkspaceMemberDto>, canManage: Boolean, nav: (String) -> Unit, index: Int) {
    val active = members.filter { it.status == MemberStatusDto.ACTIVE }
    HmDiv(
        HomeClasses.PANEL, HomeClasses.LIFT, HomeClasses.PAD, HomeClasses.SPAN_4,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead("Team", BrandHue.SLACK, { LucideUsers(size = HomeArt.ICON_PX) })
        StateSwap(state = active.isEmpty()) {
            if (active.isEmpty()) {
                HmEmpty(
                    title = "No people yet",
                    subtitle = "Invite your team to collect updates.",
                    actionLabel = if (canManage) "Invite people" else null,
                    onAction = { nav("/app/settings/members") },
                ) {
                    HmDiv(HomeClasses.STACK) {
                        repeat(HomeArt.STACK_GHOSTS) { HmDiv(HomeClasses.STACK_ITEM) { HmDiv(HomeClasses.GHOST_AVATAR) {} } }
                    }
                    HmGhost(widthPercent = 60, height = "0.625rem")
                }
            } else {
                TeamBody(active, canManage, nav)
            }
        }
    }
}

@Composable
private fun TeamBody(active: List<WorkspaceMemberDto>, canManage: Boolean, nav: (String) -> Unit) {
    val shown = active.take(OVERFLOW_AVATAR_LIMIT)
    val rest = active.size - shown.size
    val linked = active.count { it.linearUserId != null }
    HmDiv(modifier = Modifier.stack()) {
        HmDiv(HomeClasses.STACK) {
            shown.forEach { member ->
                HmDiv(HomeClasses.STACK_ITEM) {
                    Avatar(name = member.displayName, src = member.avatarUrl, sizePx = HomeArt.AVATAR_PX)
                }
            }
            if (rest > 0) HmDiv(HomeClasses.STACK_ITEM, HomeClasses.STACK_MORE) { Text("+$rest") }
        }
        HmDiv(HomeClasses.STACK_FACTS) {
            HmStrong(if (active.size == 1) "1 person" else "${active.size} people")
            HmMuted("$linked linked to Linear")
        }
        if (canManage) {
            HmDiv(modifier = Modifier.rowWrap()) {
                Button(
                    label = "Manage people",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    onClick = { nav("/app/settings/members") },
                )
            }
        }
    }
}

private data class QuickAction(val label: String, val path: String, val tint: String, val admin: Boolean)

private val QUICK_ACTIONS = listOf(
    QuickAction("New standup", "/app/standups/new", BrandHue.LINEAR, admin = true),
    QuickAction("Run history", "/app/runs", BrandHue.SLACK, admin = false),
    QuickAction("Templates", "/app/templates", BrandHue.CLAUDE, admin = true),
    QuickAction("Integrations", "/app/settings/integrations", BrandHue.GOOGLE, admin = true),
    QuickAction("Standups", "/app/standups", BrandHue.LINEAR, admin = false),
    QuickAction("Releases", "/app/releases", BrandHue.CLAUDE, admin = false),
)

@Composable
private fun QuickActionsCard(canManage: Boolean, index: Int) {
    val actions = QUICK_ACTIONS.filter { canManage || !it.admin }.take(HomeArt.QUICK_ACTIONS_SHOWN)
    HmDiv(
        HomeClasses.PANEL, HomeClasses.PAD, HomeClasses.SPAN_4,
        modifier = Modifier.staggerIn(index),
    ) {
        HmCardHead("Quick actions", Palette.accent.cssRgb(), { LucidePlay(size = HomeArt.ICON_PX) })
        HmDiv(HomeClasses.TILES) {
            actions.forEach { action ->
                HmLink(action.path, HomeClasses.TILE) {
                    HmIconTile(action.tint) { QuickIcon(action) }
                    HmDiv(HomeClasses.TILE_LABEL) { Text(action.label) }
                }
            }
        }
    }
}

@Composable
private fun QuickIcon(action: QuickAction) {
    when (action.path) {
        "/app/standups/new" -> LucideMessagesSquare(size = HomeArt.ICON_PX)
        "/app/runs" -> LucideClock(size = HomeArt.ICON_PX)
        "/app/templates" -> LucideFileText(size = HomeArt.ICON_PX)
        "/app/settings/integrations" -> LucidePlug(size = HomeArt.ICON_PX)
        "/app/releases" -> LucideTag(size = HomeArt.ICON_PX)
        else -> LucideCalendar(size = HomeArt.ICON_PX)
    }
}

@Composable
private fun ChannelChip(name: String) {
    HmDiv(HomeClasses.CHANNEL) {
        Img(src = SLACK_LOGO, attrs = Modifier.toAttrs { attr("alt", "") })
        SpanText(
            text = "#$name",
            modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textSecondary),
        )
    }
}

private val DAY_LETTERS = listOf("M", "T", "W", "T", "F", "S", "S")
private val DAY_NAMES = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

private fun shortTimezone(raw: String): String = raw.substringAfterLast("/").replace("_", " ").ifBlank { raw }

private fun sectionsLabel(count: Int): String = if (count == 1) "1 person" else "$count people"

private fun postedLabel(total: Int): String = "$total posted"
